import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { normalizePhone } from '../../util/util'
import { SubmitButton } from './ScheduleServiceMultiStep/Wizard'
import gql from 'graphql-tag'
import { newBackendClient } from '../clients/new-backend.client'
import { Mutation, MutationDiscountTireContactUsArgs } from '../../generated/nest-graphql'
import { useRouter } from 'next/router'

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

const schema = yup
  .object({
    name: yup.string().required('Required'),
    email: yup.string().email().required('Required'),
    phoneNumber: yup.string().matches(phoneRegExp, { message: 'Invalid Phone Number' }).required('Required'),
    metro: yup.string().required('Required'),
    inquiry: yup.string().min(1).required('Required'),
    dtAccountNumber: yup.string(),
    appointmentRequestDate: yup.date(),
  })
  .required()

export function DiscountTireForm() {
  const something = useRouter()
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  })
  const onSubmit = async (data) => {
    await newBackendClient.mutate<Mutation, MutationDiscountTireContactUsArgs>({
      variables: {
        discountTireContactUsInput: data,
      },
      mutation: gql`
        mutation dt($discountTireContactUsInput: DiscountTireContactUsInput!) {
          discountTireContactUs(discountTireContactUsInput: $discountTireContactUsInput) {
            name
            email
            phoneNumber
            metro
            inquiry
            appointmentRequestDate
            dtAccountNumber
          }
        }
      `,
    })
    return await something.push('/discount-tire-success')
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 gap-4">
        <Input
          type="text"
          control={control}
          label={'Discount Tire Account Number'}
          name="dtAccountNumber"
          required={false}
        />
        <Input type="text" control={control} label={'Name'} name="name" required={true} />
        <Input control={control} type="email" label={'Email'} name="email" required={true} />
        <Input control={control} type="phoneNumber" name="phoneNumber" required={true} label={'Phone Number'} />
        <Input
          control={control}
          label={'Metro/City'}
          name="metro"
          required={true}
          type="select"
          options={['Not Listed', 'Austin', 'Houston', 'Dallas', 'San Antonio', 'Atlanta', 'Nashville']}
        />
        <Input
          control={control}
          label="Service Inquiry"
          description="How can we help? Please provide a description of the services you are looking for. Please also provide a list of vehicles you would like serviced."
          name="inquiry"
          required={true}
          type="textarea"
        />
        <Input
          control={control}
          type="datetime-local"
          name="appointmentRequestDate"
          required={false}
          label={'Appointment Request Date'}
        />
        <SubmitButton isSubmitting={isSubmitting} buttonText="Get A Quote Today!" />
      </div>
    </form>
  )
}

type CommonProps = {
  name: string
  control: any
  label: string
  required: boolean
  description?: string
}

type NonCommonProps =
  | {
      type: 'text' | 'email' | 'textarea' | 'phoneNumber' | 'select' | 'datetime-local'
    }
  | {
      type: 'select'
      options: string[]
    }

type InputProps = CommonProps & NonCommonProps

export function Input({ name, control, label, required, type = 'text', description, ...rest }: InputProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        return (
          <label className="block">
            <span className="text-gray-700 font-bold">{label}</span>
            {required && <span className="text-red-400"> *</span>}
            {description && (
              <div>
                <span className="text-gray-500">{description}</span>
              </div>
            )}
            {type === 'datetime-local' ? (
              <input
                type={type}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                {...field}
              />
            ) : type === 'textarea' ? (
              <textarea
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                {...field}
              />
            ) : type === 'phoneNumber' ? (
              <input
                type={'text'}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                {...field}
                onChange={(e) => {
                  const val = normalizePhone(e.target.value)
                  field.onChange(val)
                }}
              />
            ) : type === 'select' ? (
              <select
                className="block
              w-full
              mt-1
              rounded-md
              border-gray-300
              shadow-sm
              focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                {...field}
              >
                {(rest as any)?.options?.map((val) => {
                  return (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  )
                })}
              </select>
            ) : (
              <input
                type={type}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                {...field}
              />
            )}
            {fieldState.error?.message && <span className="text-red-500">{fieldState.error.message}</span>}
          </label>
        )
      }}
    />
  )
}
